import { makeAutoObservable, reaction, runInAction } from 'mobx';
import moment from 'moment';

import { client } from 'contexts/ApolloClient/apolloClient';
import { standardError } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { getBusinessWeekRange, toRequestLocalDate } from 'utils/datetime';

import { FETCH_ASSIGNMENTS_WITH_FILTERS } from './gql/workAssignmentSearch';
import { MOCK_ASSIGNMENTS } from './mock';

export const RECONCILIATION_STATUS = {
	all: {
		label: 'All',
		value: null,
	},
	auto: {
		label: 'Auto',
		value: 'auto',
	},
	manual: {
		label: 'Manual',
		value: 'manual',
	},
	failed: {
		label: 'Failed',
		value: 'failed',
	},
	pending: {
		label: 'Pending',
		value: 'pending',
	},
};

export const TABS_MODS_VALS = {
	all: {
		id: 'mode_all',
		value: 'all',
	},
	problematic: {
		id: 'mode_problematic',
		value: 'problematic',
	},
	undefined: {
		id: 'mode_undefined',
		value: 'undefined',
	},
	mocked: {
		id: 'mode_mocked',
		value: null,
	},
};

export const TABS_MODS = {
	all: {
		label: 'All',
		tabId: TABS_MODS_VALS.all.id,
		value: TABS_MODS_VALS.all.value,
		panelId: 'assignments-all',
	},
	problematic: {
		label: 'Problematic',
		tabId: TABS_MODS_VALS.problematic.id,
		value: TABS_MODS_VALS.problematic.value,
		panelId: 'assignments-problematic',
	},
	undefined: {
		label: 'Undefined',
		tabId: TABS_MODS_VALS.undefined.id,
		value: TABS_MODS_VALS.undefined.value,
		panelId: 'assignments-undefined',
	},
	// mockedData: {
	// 	label: 'Mocked data',
	// 	tabId: TABS_MODS_VALS.mocked.id,
	// 	value: TABS_MODS_VALS.mocked.value,
	// 	panelId: 'assignments-mocked',
	// },
};

/**
 * @typedef {{ id: string, name: string }} IdNameOption
 * @typedef {{ fromDate: string | null, toDate: string | null }} DateRange
 * @typedef {{ value: string | null, label: string }} ReconciliationStatusOption
 * @typedef {{ size: number, page: number, totalPages: number, totalCount: number }} PaginationInfo
 *
 * @typedef {Object} WorkAssignmentFilters
 * @property {string[]} runNames
 * @property {string[]} tripNumbers
 * @property {string} assignee
 * @property {IdNameOption | null} crewMember
 * @property {IdNameOption[]} mobilityPartners
 * @property {IdNameOption[]} cities
 * @property {DateRange} dateRange
 * @property {ReconciliationStatusOption} reconciliationStatus
 *
 * @typedef {Object} WorkAssignmentsPersistedState
 * @property {WorkAssignmentFilters} filters
 * @property {number} activePage
 * @property {number} size
 * @property {string} currentSearchModeKey
 */

const FILTER_KEYS = {
	runNames: 'runNames',
	tripNumbers: 'tripNumbers',
	assignee: 'assignee',
	crewMember: 'crewMember',
	mobilityPartners: 'mobilityPartners',
	cities: 'cities',
	dateRange: 'dateRange',
	reconciliationStatus: 'reconciliationStatus',
};

const FILTER_OPERATIONS = {
	update: 'update',
	addItem: 'addItem',
	removeItem: 'removeItem',
	reset: 'reset',
};

const COLLECTION_FIELDS = new Set([
	FILTER_KEYS.runNames,
	FILTER_KEYS.tripNumbers,
	FILTER_KEYS.mobilityPartners,
	FILTER_KEYS.cities,
]);

const DEFAULT_SEARCH_MODE_KEY = 'all';

const LOCAL_STORAGE_KEYS = {
	workAssignmentsState: 'workAssignmentsState',
};

/**
 * @param {{ businessWeekStart: string, businessWeekEnd: string }} weekRange
 * @returns {WorkAssignmentFilters}
 */
function createDefaultFilters(weekRange) {
	return {
		runNames: [],
		tripNumbers: [],
		assignee: '',
		crewMember: null,
		mobilityPartners: [],
		cities: [],
		dateRange: {
			fromDate: weekRange.businessWeekStart,
			toDate: weekRange.businessWeekEnd,
		},
		reconciliationStatus: RECONCILIATION_STATUS.all,
	};
}

/**
 * @returns {PaginationInfo}
 */
function createDefaultPaginationInfo() {
	return {
		size: 10,
		page: 0,
		totalPages: 0,
		totalCount: 0,
	};
}

/**
 * @param {unknown} value
 * @returns {value is IdNameOption}
 */
function isIdNameOption(value) {
	return Boolean(
		value &&
			typeof value === 'object' &&
			typeof value.id === 'string' &&
			typeof value.name === 'string'
	);
}

/**
 * @param {unknown} value
 * @returns {value is ReconciliationStatusOption}
 */
function isStatusOption(value) {
	return Boolean(value && typeof value === 'object' && 'label' in value && 'value' in value);
}

/**
 * @param {unknown} value
 * @returns {value is DateRange}
 */
function isDateRange(value) {
	return Boolean(value && typeof value === 'object' && 'fromDate' in value && 'toDate' in value);
}

/**
 * @param {string[]} arr
 * @returns {string[]}
 */
function getUniqueStringsPreserveOrder(arr) {
	const seen = new Set();
	const result = [];

	arr.forEach((item) => {
		if (seen.has(item)) return;
		seen.add(item);
		result.push(item);
	});

	return result;
}

/**
 * @param {IdNameOption[]} arr
 * @returns {IdNameOption[]}
 */
function getUniqueObjectsByIdPreserveOrder(arr) {
	const seen = new Set();
	const result = [];

	arr.forEach((item) => {
		if (!isIdNameOption(item)) return;
		if (seen.has(item.id)) return;

		seen.add(item.id);
		result.push({
			id: item.id,
			name: item.name,
		});
	});

	return result;
}

/**
 * Order-insensitive signature for backend request meaning.
 *
 * @param {string[]} arr
 * @returns {string}
 */
function buildStringArrayQuerySignature(arr) {
	return [...new Set(arr)].sort().join('|');
}

/**
 * Order-insensitive signature for backend request meaning.
 *
 * @param {IdNameOption[]} arr
 * @returns {string}
 */
function buildIdArrayQuerySignature(arr) {
	return [...new Set(arr.map((item) => item.id))].sort().join('|');
}

/**
 * Store-level equality, order-sensitive.
 *
 * @param {string[]} left
 * @param {string[]} right
 * @returns {boolean}
 */
function areOrderedStringArraysEqual(left, right) {
	if (left.length !== right.length) return false;

	for (let i = 0; i < left.length; i += 1) {
		if (left[i] !== right[i]) return false;
	}

	return true;
}

/**
 * Store-level equality, order-sensitive.
 *
 * @param {IdNameOption[]} left
 * @param {IdNameOption[]} right
 * @returns {boolean}
 */
function areOrderedIdArraysEqual(left, right) {
	if (left.length !== right.length) return false;

	for (let i = 0; i < left.length; i += 1) {
		if (left[i]?.id !== right[i]?.id) return false;
	}

	return true;
}

/**
 * @param {DateRange} left
 * @param {DateRange} right
 * @returns {boolean}
 */
function areDateRangesEqual(left, right) {
	return left.fromDate === right.fromDate && left.toDate === right.toDate;
}

/**
 * @param {unknown} input
 * @returns {ReconciliationStatusOption | null}
 */
function resolveReconciliationStatus(input) {
	if (input === null) {
		return RECONCILIATION_STATUS.all;
	}

	if (typeof input === 'string' && input in RECONCILIATION_STATUS) {
		return RECONCILIATION_STATUS[input];
	}

	if (isStatusOption(input)) {
		const match = Object.values(RECONCILIATION_STATUS).find(
			(option) => option.value === input.value
		);

		return match ?? null;
	}

	return null;
}

/**
 * @param {WorkAssignmentFilters} filters
 * @returns {string}
 */
function buildFiltersQuerySignature(filters) {
	return JSON.stringify({
		runNames: buildStringArrayQuerySignature(filters.runNames),
		tripNumbers: buildStringArrayQuerySignature(filters.tripNumbers),
		assignee: filters.assignee,
		crewMember: filters.crewMember?.id ?? null,
		mobilityPartners: buildIdArrayQuerySignature(filters.mobilityPartners),
		cities: buildIdArrayQuerySignature(filters.cities),
		dateRange: `${filters.dateRange.fromDate ?? ''}|${filters.dateRange.toDate ?? ''}`,
		reconciliationStatus: filters.reconciliationStatus?.value ?? null,
	});
}

/**
 * @param {WorkAssignmentsPersistedState} state
 * @returns {string}
 */
function serializePersistedState(state) {
	return JSON.stringify({
		filters: {
			runNames: state.filters.runNames,
			tripNumbers: state.filters.tripNumbers,
			assignee: state.filters.assignee,
			crewMember: state.filters.crewMember,
			mobilityPartners: state.filters.mobilityPartners,
			cities: state.filters.cities,
			dateRange: state.filters.dateRange,
			reconciliationStatus: state.filters.reconciliationStatus,
		},
		activePage: state.activePage,
		size: state.size,
		currentSearchModeKey: state.currentSearchModeKey,
	});
}

/**
 * @param {unknown} raw
 * @param {{ businessWeekStart: string, businessWeekEnd: string }} weekRange
 * @returns {WorkAssignmentsPersistedState | null}
 */
function deserializePersistedState(raw, weekRange) {
	if (!raw || typeof raw !== 'object') return null;

	const fallbackFilters = createDefaultFilters(weekRange);
	const rawFilters = raw.filters && typeof raw.filters === 'object' ? raw.filters : {};

	const runNames =
		Array.isArray(rawFilters.runNames) &&
		rawFilters.runNames.every((item) => typeof item === 'string')
			? getUniqueStringsPreserveOrder(rawFilters.runNames)
			: fallbackFilters.runNames;

	const tripNumbers = Array.isArray(rawFilters.tripNumbers)
		? getUniqueStringsPreserveOrder(
				rawFilters.tripNumbers
					.map((item) => String(item ?? '').replace(/\D+/g, ''))
					.filter((item) => item.length > 0)
			)
		: fallbackFilters.tripNumbers;

	const assignee =
		typeof rawFilters.assignee === 'string' ? rawFilters.assignee : fallbackFilters.assignee;

	const crewMember =
		rawFilters.crewMember === null
			? null
			: isIdNameOption(rawFilters.crewMember)
				? {
						id: rawFilters.crewMember.id,
						name: rawFilters.crewMember.name,
					}
				: fallbackFilters.crewMember;

	const mobilityPartners = Array.isArray(rawFilters.mobilityPartners)
		? getUniqueObjectsByIdPreserveOrder(rawFilters.mobilityPartners)
		: fallbackFilters.mobilityPartners;

	const cities = Array.isArray(rawFilters.cities)
		? getUniqueObjectsByIdPreserveOrder(rawFilters.cities)
		: fallbackFilters.cities;

	let dateRange = fallbackFilters.dateRange;
	if (isDateRange(rawFilters.dateRange)) {
		const hasBothDates = Boolean(rawFilters.dateRange.fromDate && rawFilters.dateRange.toDate);
		const hasNoDates =
			rawFilters.dateRange.fromDate === null && rawFilters.dateRange.toDate === null;

		if (hasBothDates || hasNoDates) {
			dateRange = {
				fromDate: rawFilters.dateRange.fromDate,
				toDate: rawFilters.dateRange.toDate,
			};
		}
	}

	const reconciliationStatus =
		resolveReconciliationStatus(rawFilters.reconciliationStatus) ??
		fallbackFilters.reconciliationStatus;

	const activePage = typeof raw.activePage === 'number' && raw.activePage >= 0 ? raw.activePage : 0;

	const size = typeof raw.size === 'number' && raw.size > 0 ? raw.size : 10;

	const currentSearchModeKey =
		typeof raw.currentSearchModeKey === 'string' && raw.currentSearchModeKey in TABS_MODS
			? raw.currentSearchModeKey
			: DEFAULT_SEARCH_MODE_KEY;

	return {
		filters: {
			runNames,
			tripNumbers,
			assignee,
			crewMember,
			mobilityPartners,
			cities,
			dateRange,
			reconciliationStatus,
		},
		activePage,
		size,
		currentSearchModeKey,
	};
}

/**
 * @param {{ businessWeekStart: string, businessWeekEnd: string }} weekRange
 * @returns {WorkAssignmentsPersistedState | null}
 */
function getPersistedState(weekRange) {
	try {
		if (typeof localStorage === 'undefined') return null;

		const rawValue = localStorage.getItem(LOCAL_STORAGE_KEYS.workAssignmentsState);
		if (!rawValue) return null;

		return deserializePersistedState(JSON.parse(rawValue), weekRange);
	} catch (error) {
		console.error('[WorkAssignmentsStore] failed to read persisted state', error);
		return null;
	}
}

/**
 * @param {WorkAssignmentsPersistedState} state
 */
function persistState(state) {
	try {
		if (typeof localStorage === 'undefined') return;

		localStorage.setItem(LOCAL_STORAGE_KEYS.workAssignmentsState, serializePersistedState(state));
	} catch (error) {
		console.error('[WorkAssignmentsStore] failed to persist state', error);
	}
}

/**
 * @param {DateRange} dateRange
 * @returns {{ fromDate: string, toDate: string } | null}
 */
function buildRequestDateRange(dateRange) {
	const fromDate = toRequestLocalDate(dateRange?.fromDate);
	const toDate = toRequestLocalDate(dateRange?.toDate);

	if (!fromDate || !toDate) return null;

	return {
		fromDate,
		toDate,
	};
}

export class WorkAssignmentsStore {
	assignments = [];
	/**
	 * Set of `sequenceGroupId`s whose cards are currently expanded on the
	 * list. Kept on the store (not per-card `useState`) so external events
	 * — chiefly a fresh breakdown — can toggle a specific card open.
	 * Persists across `fetchAssignments` runs so filter/pagination refreshes
	 * don't collapse cards the user has opened.
	 * @type {Set<string>}
	 */
	expandedSgIds = new Set();
	pageCount = 10;
	size = 10;
	activePage = 0;
	currentPaginationInfo = createDefaultPaginationInfo();
	currentSearchModeKey = DEFAULT_SEARCH_MODE_KEY;
	isLoading = false;
	isError = false;

	requestId = 0;
	abortController = null;
	inFlightRequestSignature = null;
	lastCompletedRequestSignature = null;

	businessWeekRange = getBusinessWeekRange();

	/** @type {WorkAssignmentFilters} */
	filters = createDefaultFilters(this.businessWeekRange);

	/** @type {null | (() => void)} */
	disposeFetchReaction = null;

	/** @type {null | (() => void)} */
	disposePersistReaction = null;

	/** @type {{ jobCode: string, sequenceGroupId: string } | null} */
	relatedAnchor = null;

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });

		const persistedState = getPersistedState(this.businessWeekRange);

		if (persistedState) {
			this.filters = persistedState.filters;
			this.activePage = persistedState.activePage;
			this.size = persistedState.size;
			this.currentSearchModeKey = persistedState.currentSearchModeKey;
		}

		this.persistCurrentState();
	}

	get currentSearchMode() {
		return TABS_MODS[this.currentSearchModeKey] ?? TABS_MODS[DEFAULT_SEARCH_MODE_KEY];
	}

	get currentActivePage() {
		return this.activePage;
	}

	get requestSignature() {
		return JSON.stringify({
			mode: this.currentSearchMode.value,
			page: this.activePage,
			size: this.size,
			filters: buildFiltersQuerySignature(this.filters),
		});
	}

	get persistedState() {
		return {
			filters: this.filters,
			activePage: this.activePage,
			size: this.size,
			currentSearchModeKey: this.currentSearchModeKey,
		};
	}

	get relatedAssignments() {
		if (!this.relatedAnchor) return null;

		const { jobCode, sequenceGroupId } = this.relatedAnchor;
		const sorted = [...this.assignments].sort((a, b) => {
			const aTime = moment(a.workAssignmentSequences?.[0]?.fromTimestamp ?? 0);
			const bTime = moment(b.workAssignmentSequences?.[0]?.fromTimestamp ?? 0);
			return aTime.diff(bTime);
		});

		// Case when there are multiple jobs and we have to sort them in logical sequence by prev and next date
		const anchorIndex = sorted.findIndex((a) => a.sequenceGroupId === sequenceGroupId);
		if (anchorIndex === -1) return sorted;

		let left = anchorIndex;
		let right = anchorIndex;

		while (left > 0) {
			if (sorted[left - 1].job?.code === jobCode) {
				left -= 1;
				break;
			}
			left -= 1;
		}

		while (right < sorted.length - 1) {
			if (sorted[right + 1].job?.code === jobCode) break;
			right += 1;
		}

		return sorted.slice(left, right + 1);
	}

	start() {
		if (this.disposeFetchReaction) return;

		this.disposeFetchReaction = reaction(
			() => [
				this.currentSearchModeKey,
				this.activePage,
				this.size,
				buildFiltersQuerySignature(this.filters),
			],
			() => {
				this.fetchAssignments();
			},
			{ fireImmediately: true }
		);
	}

	destroy() {
		this.cancelOngoingRequest();
		this.disposeFetchReaction?.();
		this.disposeFetchReaction = null;
	}

	/**
	 * @param {{ tabId: string }} mode
	 */
	setSearchMode(mode) {
		const nextKey =
			Object.keys(TABS_MODS).find((key) => TABS_MODS[key].tabId === mode.tabId) ??
			DEFAULT_SEARCH_MODE_KEY;

		if (this.currentSearchModeKey === nextKey) {
			return;
		}

		this.currentSearchModeKey = nextKey;
		this.activePage = 0;
		this.persistCurrentState();
	}

	/**
	 * @param {number} page
	 */
	setActivePage(page) {
		if (this.activePage === page) return;
		this.activePage = page;
		this.persistCurrentState();
	}

	/**
	 * @param {number} page
	 */
	setCurrentActivePage(page) {
		this.setActivePage(page);
	}

	/**
	 * @param {number} size
	 */
	setPageSize(size) {
		if (this.size === size) return;
		this.size = size;
		this.activePage = 0;
		this.persistCurrentState();
	}

	resetAllFilters() {
		const nextFilters = createDefaultFilters(this.businessWeekRange);

		if (buildFiltersQuerySignature(this.filters) === buildFiltersQuerySignature(nextFilters)) {
			console.warn('[WorkAssignmentsStore] resetAllFilters noop');
			return;
		}

		this.relatedAnchor = null;
		this.filters = nextFilters;
		this.activePage = 0;
		this.persistCurrentState();
	}

	/**
	 * @param {{ runName: string | null, ridePlannedDeparture: string | null, jobCode: string | null, sequenceGroupId: string | null }} payload
	 */
	prefillRelatedAssignments({ runName, ridePlannedDeparture, jobCode, sequenceGroupId }) {
		const departure = ridePlannedDeparture ? moment(ridePlannedDeparture) : moment();

		const dayBefore = departure.clone().subtract(1, 'day').startOf('day').toISOString();
		const dayAfter = departure.clone().add(1, 'day').endOf('day').toISOString();

		this.relatedAnchor = jobCode && sequenceGroupId ? { jobCode, sequenceGroupId } : null;

		this.filters = {
			...createDefaultFilters(this.businessWeekRange),
			runNames: runName ? [runName] : [],
			dateRange: { fromDate: dayBefore, toDate: dayAfter },
		};
		this.activePage = 0;
		this.persistCurrentState();
	}

	/**
	 * Generic whole-field update.
	 *
	 * @param {keyof WorkAssignmentFilters} field
	 * @param {unknown} value
	 */
	updateFilter(field, value) {
		if (!this.isOperationAllowed(field, FILTER_OPERATIONS.update)) {
			console.warn(`[WorkAssignmentsStore] updateFilter not allowed for field: ${field}`);
			return;
		}

		const nextValue = this.normalizeFieldValue(field, value);

		if (nextValue === undefined) {
			console.warn(`[WorkAssignmentsStore] updateFilter invalid value for field: ${field}`, value);
			return;
		}

		if (this.isFieldValueEqual(field, this.filters[field], nextValue)) {
			console.warn(`[WorkAssignmentsStore] updateFilter noop for field: ${field}`);
			return;
		}

		this.relatedAnchor = null;
		this.filters[field] = nextValue;
		this.activePage = 0;
		this.persistCurrentState();
	}

	/**
	 * Generic incremental add for collection filters.
	 *
	 * @param {'runNames' | 'mobilityPartners' | 'cities'} field
	 * @param {unknown} item
	 */
	addFilterItem(field, item) {
		if (!this.isOperationAllowed(field, FILTER_OPERATIONS.addItem)) {
			console.warn(`[WorkAssignmentsStore] addFilterItem not allowed for field: ${field}`);
			return;
		}

		const normalizedItem = this.normalizeCollectionItem(field, item);

		if (normalizedItem === undefined) {
			console.warn(`[WorkAssignmentsStore] addFilterItem invalid item for field: ${field}`, item);
			return;
		}

		if (this.hasCollectionItem(field, normalizedItem)) {
			console.warn(`[WorkAssignmentsStore] addFilterItem duplicate for field: ${field}`, item);
			return;
		}

		this.relatedAnchor = null;
		this.filters[field] = [...this.filters[field], normalizedItem];
		this.activePage = 0;
		this.persistCurrentState();
	}

	/**
	 * Generic incremental remove for collection filters.
	 *
	 * @param {'runNames' | 'mobilityPartners' | 'cities'} field
	 * @param {unknown} item
	 */
	removeFilterItem(field, item) {
		if (!this.isOperationAllowed(field, FILTER_OPERATIONS.removeItem)) {
			console.warn(`[WorkAssignmentsStore] removeFilterItem not allowed for field: ${field}`);
			return;
		}

		const normalizedItem = this.normalizeCollectionItem(field, item);

		if (normalizedItem === undefined) {
			console.warn(
				`[WorkAssignmentsStore] removeFilterItem invalid item for field: ${field}`,
				item
			);
			return;
		}

		if (!this.hasCollectionItem(field, normalizedItem)) {
			console.warn(
				`[WorkAssignmentsStore] removeFilterItem missing item for field: ${field}`,
				item
			);
			return;
		}

		this.relatedAnchor = null;

		if (field === FILTER_KEYS.runNames || field === FILTER_KEYS.tripNumbers) {
			this.filters[field] = this.filters[field].filter((value) => value !== normalizedItem);
		} else {
			this.filters[field] = this.filters[field].filter((value) => value.id !== normalizedItem.id);
		}

		this.activePage = 0;
		this.persistCurrentState();
	}

	/**
	 * @param {keyof WorkAssignmentFilters} field
	 */
	resetFilter(field) {
		if (!this.isOperationAllowed(field, FILTER_OPERATIONS.reset)) {
			console.warn(`[WorkAssignmentsStore] resetFilter not allowed for field: ${field}`);
			return;
		}

		const defaultValue = this.getDefaultFieldValue(field);

		if (this.isFieldValueEqual(field, this.filters[field], defaultValue)) {
			console.warn(`[WorkAssignmentsStore] resetFilter noop for field: ${field}`);
			return;
		}

		this.relatedAnchor = null;
		this.filters[field] = defaultValue;
		this.activePage = 0;
		this.persistCurrentState();
	}

	buildFilters() {
		/** @type {Record<string, unknown>} */
		const filter = {};

		if (this.filters.runNames.length > 0) {
			filter.runNames = this.filters.runNames;
		}

		if (this.filters.tripNumbers.length > 0) {
			const parsedTripNumbers = this.filters.tripNumbers
				.map((item) => Number.parseInt(item, 10))
				.filter((item) => Number.isFinite(item));

			if (parsedTripNumbers.length > 0) {
				filter.tripNumbers = parsedTripNumbers;
			}
		}

		if (this.filters.assignee) {
			filter.assignee = this.filters.assignee;
		}

		if (this.filters.crewMember?.id) {
			filter.crewMember = this.filters.crewMember.id;
		}

		if (this.filters.mobilityPartners.length > 0) {
			filter.mobilityPartnerIds = this.filters.mobilityPartners.map((item) => item.id);
		}

		if (this.filters.cities.length > 0) {
			filter.cityIds = this.filters.cities.map((item) => item.id);
		}

		const requestDateRange = buildRequestDateRange(this.filters.dateRange);

		if (requestDateRange) {
			filter.fromDate = requestDateRange.fromDate;
			filter.toDate = requestDateRange.toDate;
		}

		if (this.filters.reconciliationStatus.value !== null) {
			filter.reconciliationStatus = this.filters.reconciliationStatus.value;
		}

		if (this.currentSearchMode.value !== null) {
			filter.mode = this.currentSearchMode.value;
		}

		return filter;
	}

	cancelOngoingRequest() {
		if (this.abortController) {
			this.abortController.abort();
			this.abortController = null;
		}
	}

	async fetchAssignments() {
		const requestSignature = this.requestSignature;

		if (this.isLoading && this.inFlightRequestSignature === requestSignature) {
			return;
		}

		const currentRequestId = ++this.requestId;

		if (this.inFlightRequestSignature !== requestSignature) {
			this.cancelOngoingRequest();
		}

		this.isLoading = true;
		this.isError = false;
		this.inFlightRequestSignature = requestSignature;

		if (this.currentSearchMode.value === TABS_MODS_VALS.mocked.value) {
			await new Promise((res) => setTimeout(res, 500));

			if (currentRequestId !== this.requestId) return;

			runInAction(() => {
				this.assignments = MOCK_ASSIGNMENTS;
				this.pageCount = 1;
				this.currentPaginationInfo = {
					size: this.size,
					page: this.activePage,
					totalPages: 1,
					totalCount: MOCK_ASSIGNMENTS.length,
				};
				this.isLoading = false;
				this.abortController = null;
				this.inFlightRequestSignature = null;
				this.lastCompletedRequestSignature = requestSignature;
			});

			return;
		}

		this.abortController = new AbortController();

		try {
			const { data } = await client.query({
				query: FETCH_ASSIGNMENTS_WITH_FILTERS,
				variables: {
					pagination: {
						page: this.activePage,
						size: this.size,
					},
					filter: this.buildFilters(),
				},
				fetchPolicy: 'no-cache',
				// context: {
				// 	fetchOptions: {
				// 		signal: this.abortController.signal,
				// 	},
				// },
			});

			if (currentRequestId !== this.requestId) return;

			runInAction(() => {
				const results = data?.workAssignmentSearchV2?.results ?? [];
				const pageInfo = data?.workAssignmentSearchV2?.page ?? createDefaultPaginationInfo();

				this.assignments = results;
				this.pageCount = pageInfo.totalPages ?? 0;
				this.currentPaginationInfo = {
					size: pageInfo.size ?? this.size,
					page: pageInfo.page ?? this.activePage,
					totalPages: pageInfo.totalPages ?? 0,
					totalCount: pageInfo.totalCount ?? 0,
				};
				this.isLoading = false;
				this.isError = false;
				this.abortController = null;
				this.inFlightRequestSignature = null;
				this.lastCompletedRequestSignature = requestSignature;
			});
		} catch (error) {
			if (currentRequestId !== this.requestId) return;

			if (error?.name === 'AbortError') {
				runInAction(() => {
					this.abortController = null;
					this.inFlightRequestSignature = null;
					this.isLoading = false;
				});
				return;
			}

			runInAction(() => {
				this.isLoading = false;
				this.isError = true;
				this.abortController = null;
				this.inFlightRequestSignature = null;
			});

			notificationStore.setNotification(standardError);
		}
	}

	/**
	 * @param {keyof WorkAssignmentFilters} field
	 * @param {string} operation
	 * @returns {boolean}
	 */
	isOperationAllowed(field, operation) {
		const config = this.getFieldConfig(field);
		return config.allowedOperations.includes(operation);
	}

	/**
	 * @param {keyof WorkAssignmentFilters} field
	 * @returns {{
	 *   allowedOperations: string[],
	 *   defaultValue: () => WorkAssignmentFilters[keyof WorkAssignmentFilters]
	 * }}
	 */
	getFieldConfig(field) {
		const configs = {
			[FILTER_KEYS.runNames]: {
				allowedOperations: [
					FILTER_OPERATIONS.update,
					FILTER_OPERATIONS.addItem,
					FILTER_OPERATIONS.removeItem,
					FILTER_OPERATIONS.reset,
				],
				defaultValue: () => [],
			},
			[FILTER_KEYS.tripNumbers]: {
				allowedOperations: [
					FILTER_OPERATIONS.update,
					FILTER_OPERATIONS.addItem,
					FILTER_OPERATIONS.removeItem,
					FILTER_OPERATIONS.reset,
				],
				defaultValue: () => [],
			},
			[FILTER_KEYS.assignee]: {
				allowedOperations: [FILTER_OPERATIONS.update, FILTER_OPERATIONS.reset],
				defaultValue: () => '',
			},
			[FILTER_KEYS.crewMember]: {
				allowedOperations: [FILTER_OPERATIONS.update, FILTER_OPERATIONS.reset],
				defaultValue: () => null,
			},
			[FILTER_KEYS.mobilityPartners]: {
				allowedOperations: [
					FILTER_OPERATIONS.update,
					FILTER_OPERATIONS.addItem,
					FILTER_OPERATIONS.removeItem,
					FILTER_OPERATIONS.reset,
				],
				defaultValue: () => [],
			},
			[FILTER_KEYS.cities]: {
				allowedOperations: [
					FILTER_OPERATIONS.update,
					FILTER_OPERATIONS.addItem,
					FILTER_OPERATIONS.removeItem,
					FILTER_OPERATIONS.reset,
				],
				defaultValue: () => [],
			},
			[FILTER_KEYS.dateRange]: {
				allowedOperations: [FILTER_OPERATIONS.update, FILTER_OPERATIONS.reset],
				defaultValue: () => ({
					fromDate: this.businessWeekRange.businessWeekStart,
					toDate: this.businessWeekRange.businessWeekEnd,
				}),
			},
			[FILTER_KEYS.reconciliationStatus]: {
				allowedOperations: [FILTER_OPERATIONS.update, FILTER_OPERATIONS.reset],
				defaultValue: () => RECONCILIATION_STATUS.all,
			},
		};

		return configs[field];
	}

	/**
	 * @param {keyof WorkAssignmentFilters} field
	 * @returns {WorkAssignmentFilters[keyof WorkAssignmentFilters]}
	 */
	getDefaultFieldValue(field) {
		return this.getFieldConfig(field).defaultValue();
	}

	/**
	 * @param {keyof WorkAssignmentFilters} field
	 * @param {unknown} value
	 * @returns {WorkAssignmentFilters[keyof WorkAssignmentFilters] | undefined}
	 */
	normalizeFieldValue(field, value) {
		switch (field) {
			case FILTER_KEYS.runNames: {
				if (!Array.isArray(value)) return undefined;
				if (!value.every((item) => typeof item === 'string')) return undefined;
				return getUniqueStringsPreserveOrder(value);
			}

			case FILTER_KEYS.tripNumbers: {
				if (!Array.isArray(value)) return undefined;

				const sanitized = value
					.map((item) => String(item ?? '').replace(/\D+/g, ''))
					.filter((item) => item.length > 0);

				return getUniqueStringsPreserveOrder(sanitized);
			}

			case FILTER_KEYS.assignee: {
				return typeof value === 'string' ? value : undefined;
			}

			case FILTER_KEYS.crewMember: {
				if (value === null) return null;
				if (!isIdNameOption(value)) return undefined;

				return {
					id: value.id,
					name: value.name,
				};
			}

			case FILTER_KEYS.mobilityPartners:
			case FILTER_KEYS.cities: {
				if (!Array.isArray(value)) return undefined;
				return getUniqueObjectsByIdPreserveOrder(value);
			}

			case FILTER_KEYS.dateRange: {
				if (!isDateRange(value)) return undefined;

				const hasBothDates = Boolean(value.fromDate && value.toDate);
				const hasNoDates = value.fromDate === null && value.toDate === null;

				if (!hasBothDates && !hasNoDates) return undefined;

				return {
					fromDate: value.fromDate,
					toDate: value.toDate,
				};
			}

			case FILTER_KEYS.reconciliationStatus: {
				const resolved = resolveReconciliationStatus(value);
				return resolved ?? undefined;
			}

			default:
				return undefined;
		}
	}

	/**
	 * @param {'runNames' | 'mobilityPartners' | 'cities'} field
	 * @param {unknown} item
	 * @returns {string | IdNameOption | undefined}
	 */
	normalizeCollectionItem(field, item) {
		if (!COLLECTION_FIELDS.has(field)) return undefined;

		if (field === FILTER_KEYS.runNames) {
			return typeof item === 'string' ? item : undefined;
		}

		if (field === FILTER_KEYS.tripNumbers) {
			const sanitized = String(item ?? '').replace(/\D+/g, '');
			return sanitized.length > 0 ? sanitized : undefined;
		}

		if (field === FILTER_KEYS.mobilityPartners || field === FILTER_KEYS.cities) {
			if (!isIdNameOption(item)) return undefined;

			return {
				id: item.id,
				name: item.name,
			};
		}

		return undefined;
	}

	/**
	 * @param {'runNames' | 'mobilityPartners' | 'cities'} field
	 * @param {string | IdNameOption} item
	 * @returns {boolean}
	 */
	hasCollectionItem(field, item) {
		if (field === FILTER_KEYS.runNames) {
			return this.filters.runNames.includes(/** @type {string} */ (item));
		}

		if (field === FILTER_KEYS.tripNumbers) {
			return this.filters.tripNumbers.includes(/** @type {string} */ (item));
		}

		const typedItem = /** @type {IdNameOption} */ (item);
		return this.filters[field].some((value) => value.id === typedItem.id);
	}

	/**
	 * @param {keyof WorkAssignmentFilters} field
	 * @param {WorkAssignmentFilters[keyof WorkAssignmentFilters]} currentValue
	 * @param {WorkAssignmentFilters[keyof WorkAssignmentFilters]} nextValue
	 * @returns {boolean}
	 */
	isFieldValueEqual(field, currentValue, nextValue) {
		switch (field) {
			case FILTER_KEYS.runNames:
			case FILTER_KEYS.tripNumbers:
				return areOrderedStringArraysEqual(
					/** @type {string[]} */ (currentValue),
					/** @type {string[]} */ (nextValue)
				);

			case FILTER_KEYS.assignee:
				return currentValue === nextValue;

			case FILTER_KEYS.crewMember:
				return (currentValue?.id ?? null) === (nextValue?.id ?? null);

			case FILTER_KEYS.mobilityPartners:
			case FILTER_KEYS.cities:
				return areOrderedIdArraysEqual(
					/** @type {IdNameOption[]} */ (currentValue),
					/** @type {IdNameOption[]} */ (nextValue)
				);

			case FILTER_KEYS.dateRange:
				return areDateRangesEqual(
					/** @type {DateRange} */ (currentValue),
					/** @type {DateRange} */ (nextValue)
				);

			case FILTER_KEYS.reconciliationStatus:
				return (currentValue?.value ?? null) === (nextValue?.value ?? null);

			default:
				return false;
		}
	}

	toQueryParams() {
		const params = {};

		if (this.filters.runNames.length > 0) {
			params.runNames = this.filters.runNames.join(',');
		}

		if (this.filters.tripNumbers.length > 0) {
			params.tripNumbers = this.filters.tripNumbers.join(',');
		}

		if (this.filters.assignee) {
			params.assignee = this.filters.assignee;
		}

		if (this.filters.crewMember) {
			params.crewMemberId = this.filters.crewMember.id;
			params.crewMemberName = this.filters.crewMember.name;
		}

		if (this.filters.mobilityPartners.length > 0) {
			params.mobilityPartners = this.filters.mobilityPartners.map((mp) => mp.id).join(',');
			params.mobilityPartnerNames = this.filters.mobilityPartners.map((mp) => mp.name).join(',');
		}

		if (this.filters.cities.length > 0) {
			params.cities = this.filters.cities.map((city) => city.id).join(',');
			params.cityNames = this.filters.cities.map((city) => city.name).join(',');
		}

		const from = toRequestLocalDate(this.filters.dateRange.fromDate);
		const to = toRequestLocalDate(this.filters.dateRange.toDate);
		if (from) params.fromDate = from;
		if (to) params.toDate = to;

		if (this.filters.reconciliationStatus.value !== null) {
			params.reconciliationStatus = this.filters.reconciliationStatus.value;
		}

		if (this.currentSearchModeKey !== DEFAULT_SEARCH_MODE_KEY) {
			params.tab = this.currentSearchModeKey;
		}

		if (this.activePage > 0) {
			params.page = String(this.activePage);
		}

		return params;
	}

	/**
	 * @param {URLSearchParams} searchParams
	 * @returns {boolean}
	 */
	applyQueryParams(searchParams) {
		const runNames = searchParams.get('runNames');
		const tripNumbers = searchParams.get('tripNumbers');
		const assignee = searchParams.get('assignee');
		const crewMemberId = searchParams.get('crewMemberId');
		const crewMemberName = searchParams.get('crewMemberName');
		const mobilityPartners = searchParams.get('mobilityPartners');
		const mobilityPartnerNames = searchParams.get('mobilityPartnerNames');
		const cities = searchParams.get('cities');
		const cityNames = searchParams.get('cityNames');
		const fromDate = searchParams.get('fromDate');
		const toDate = searchParams.get('toDate');
		const reconciliationStatus = searchParams.get('reconciliationStatus');
		const tab = searchParams.get('tab');
		const page = searchParams.get('page');

		const hasParams = Boolean(
			runNames ||
				tripNumbers ||
				assignee ||
				crewMemberId ||
				mobilityPartners ||
				cities ||
				fromDate ||
				toDate ||
				reconciliationStatus ||
				tab ||
				page
		);

		if (!hasParams) return false;

		runInAction(() => {
			if (runNames) {
				this.filters.runNames = runNames.split(',').filter(Boolean);
			}

			if (tripNumbers) {
				this.filters.tripNumbers = tripNumbers.split(',').filter(Boolean);
			}

			if (assignee) {
				this.filters.assignee = assignee;
			}

			if (crewMemberId) {
				this.filters.crewMember = { id: crewMemberId, name: crewMemberName || '' };
			}

			if (mobilityPartners) {
				const ids = mobilityPartners.split(',').filter(Boolean);
				const names = (mobilityPartnerNames || '').split(',');
				this.filters.mobilityPartners = ids.map((id, i) => ({
					id,
					name: names[i] || '',
				}));
			}

			if (cities) {
				const ids = cities.split(',').filter(Boolean);
				const names = (cityNames || '').split(',');
				this.filters.cities = ids.map((id, i) => ({ id, name: names[i] || '' }));
			}

			if (fromDate && toDate) {
				this.filters.dateRange = {
					fromDate: new Date(fromDate).toISOString(),
					toDate: new Date(toDate).toISOString(),
				};
			}

			const resolvedStatus = reconciliationStatus
				? resolveReconciliationStatus(reconciliationStatus)
				: null;
			if (resolvedStatus) {
				this.filters.reconciliationStatus = resolvedStatus;
			}

			if (tab && tab in TABS_MODS) {
				this.currentSearchModeKey = tab;
			}

			if (page) {
				const parsed = Number(page);
				if (Number.isFinite(parsed) && parsed >= 0) {
					this.activePage = parsed;
				}
			}
		});

		this.persistCurrentState();
		return true;
	}

	persistCurrentState() {
		persistState({
			filters: this.filters,
			activePage: this.activePage,
			size: this.size,
			currentSearchModeKey: this.currentSearchModeKey,
		});
	}

	/**
	 * Splice a freshly-created breakdown into the assignment it belongs to.
	 *
	 * `createBreakdown` returns a `Breakdown` with the same shape as the
	 * `breakdown` field on `workAssignmentSearchV2` results (both pull the
	 * shared `BreakdownFields` fragment). Matching by
	 * `workAssignmentSequenceGroupId ↔ assignment.sequenceGroupId` lets us
	 * patch just that one row instead of refetching the whole page.
	 *
	 * No-op if no assignment matches — the current page might not contain
	 * the affected assignment (filter/pagination), and forcing a refetch
	 * would surprise the user by shifting the list.
	 */
	upsertBreakdownByGroupId(breakdown) {
		if (!breakdown?.workAssignmentSequenceGroupId) return;
		const sgId = breakdown.workAssignmentSequenceGroupId;
		const index = this.assignments.findIndex((a) => a?.sequenceGroupId === sgId);
		if (index === -1) return;

		runInAction(() => {
			this.assignments[index] = { ...this.assignments[index], breakdown };
			// A fresh breakdown carries information the user should see
			// immediately; auto-expand the card so they don't have to click
			// to reveal it. Subsequent manual collapse is preserved.
			this.expandedSgIds.add(sgId);
		});
	}

	isExpandedByGroupId(sgId) {
		return typeof sgId === 'string' && this.expandedSgIds.has(sgId);
	}

	toggleExpandedByGroupId(sgId) {
		if (typeof sgId !== 'string' || !sgId) return;
		runInAction(() => {
			if (this.expandedSgIds.has(sgId)) {
				this.expandedSgIds.delete(sgId);
			} else {
				this.expandedSgIds.add(sgId);
			}
		});
	}
}
